import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';
import {
    WeeklyEd15BehaviourCountsV2,
    WeeklyEd15SurveysV2,
    WeeklySeverityV2
} from '@shared/api/models/query/patient-weekly-snapshot.model';
import {WellbeingQuizModalComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/wellbeing-quiz-modal.component';
import {TrendIconComponent} from '@shared/components/trend-icon/trend-icon.component';
import {WeeklyTrendSparklineComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-trend-sparkline/weekly-trend-sparkline.component';
import {WeeklyStateLabelComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-state-label/weekly-state-label.component';

type BehaviourCountKey = keyof WeeklyEd15BehaviourCountsV2;

interface Ed15BehaviourRow {
    key: BehaviourCountKey;
    label: string;
    unit: 'time' | 'day';
    value: number | null;
}

const BEHAVIOUR_ROW_DEFS: { key: BehaviourCountKey; label: string; unit: 'time' | 'day' }[] = [
    {key: 'binge', label: 'Binge (Q11)', unit: 'time'},
    {key: 'vomited', label: 'Vomited (Q12)', unit: 'time'},
    {key: 'laxatives', label: 'Laxatives (Q13)', unit: 'day'},
    {key: 'foodRestriction', label: 'Food restriction (Q14)', unit: 'day'},
    {key: 'exercise', label: 'Exercise (Q15)', unit: 'day'},
];

/**
 * Patient weekly summary — ED-15 surveys panel (UNIVAPLAT-3035, v2 patient payload).
 *
 * Narrowed from the legacy multi-survey card (PHQ-9 / GAD-7 / ED-15 / Motivation
 * Readiness) to ED-15 only, per the v2 contract: `surveys: WeeklyEd15SurveysV2 | null`
 * is a single object, not a code-keyed map. `surveys === null` (no weekly check-in)
 * still renders the panel shell with the shared "No check-in" state — never hidden.
 *
 * Left: overall score (today's existing wellbeing badge colour) with a trend icon
 * PLUS a visible text equivalent — direction is never conveyed by the icon/colour
 * alone — and a 5-week trend sparkline from `surveys.history` (a `null` week is a
 * gap, never plotted as 0). Right: the two ED-15 subscale scores (plain, no severity
 * background — only the overall score gets the coloured chip) and the Q11-Q15
 * behaviour counts with their units ("time(s)" for Q11/Q12, "day(s)" for Q13-Q15);
 * `null` behaviour counts render "N/A" via `cdss-weekly-state-label`, never 0 — the
 * recurring HAZ-W2 theme across this Task.
 *
 * UNIVAPLAT-2360 follow-up (design correction): each subscale row also shows a trend
 * icon — this reuses `surveys.trend` (the OVERALL ED-15 trend), not an independent
 * per-subscale trend. `CheckED15Calculator` only ever computes one trend, for the
 * overall attitudinal score — weightShapeConcern/eatingConcern are scored as
 * {score, severity} only, with no trend of their own anywhere in the system. Adding
 * a genuine per-subscale trend would mean extending that clinical scoring
 * calculator (new fields, new week-over-week comparison) — confirmed out of scope
 * for this visual-parity pass; reusing the overall trend here is a deliberate,
 * confirmed simplification, not an oversight.
 *
 * The legacy (pre-regeneration) snapshot branch is wired to pass `null` here from
 * the page, rendering the same "No check-in" state until sub-task 10 (UNIVAPLAT-3042)
 * retires the legacy payload entirely.
 */
@Component({
    selector: 'cdss-weekly-survey-card',
    standalone: true,
    imports: [CommonModule, TrendIconComponent, WeeklyTrendSparklineComponent, WeeklyStateLabelComponent],
    templateUrl: './weekly-survey-card.component.html',
    styleUrl: './weekly-survey-card.component.scss'
})
export class WeeklySurveyCardComponent {

    @Input() surveys: WeeklyEd15SurveysV2 | null = null;
    @Input() patientId!: number;

    constructor(private modal: NgbModal) {
    }

    get behaviourRows(): Ed15BehaviourRow[] {
        const counts = this.surveys?.behaviourCounts;
        return BEHAVIOUR_ROW_DEFS.map(def => ({
            ...def,
            value: counts ? counts[def.key] : null,
        }));
    }

    /** "N time(s)" / "N day(s)" — only called once `row.value !== null` is confirmed by the template. */
    formatCount(row: Ed15BehaviourRow): string {
        const n = row.value as number;
        const unitLabel = row.unit === 'time'
            ? (n === 1 ? 'time' : 'times')
            : (n === 1 ? 'day' : 'days');
        return `${n} ${unitLabel}`;
    }

    /** 5-week history, oldest → current, as a null-safe ordered array for the sparkline (Ymd keys sort chronologically as strings). */
    get historyPoints(): (number | null)[] {
        const history = this.surveys?.history;
        if (!history) return [];
        return Object.keys(history).sort().map(k => history[k]);
    }

    /** Short "DD/MM" week-start labels, shown as the sparkline's visible X-axis text (UNIVAPLAT-2360 follow-up). */
    get historyLabels(): string[] {
        const history = this.surveys?.history;
        if (!history) return [];
        return Object.keys(history).sort().map(k => this.shortDateFromYmd(k));
    }

    private shortDateFromYmd(key: string): string {
        const iso = key.length === 8
            ? `${key.substring(0, 4)}-${key.substring(4, 6)}-${key.substring(6, 8)}`
            : key;
        return dayjs(iso).format('DD/MM');
    }

    /** Visible text equivalent for the trend icon — trend is never conveyed by the icon alone. */
    get trendLabel(): string | null {
        switch (this.surveys?.trend) {
            case 'up':
                return 'Increased';
            case 'down':
                return 'Decreased';
            case 'same':
                return 'No change';
            default:
                return null;
        }
    }

    severityClass(severity: WeeklySeverityV2 | null | undefined): string {
        switch (severity) {
            case 'Subclinical':
                return 'subclinical';
            case 'Moderate':
                return 'moderate';
            case 'Severe':
                return 'severe';
            case 'Extremely Severe':
                return 'extremely-severe';
            default:
                return 'unknown';
        }
    }

    openSurveyDetail(): void {
        if (!this.surveys) return;
        const ref = this.modal.open(WellbeingQuizModalComponent, {size: 'lg', centered: true});
        ref.componentInstance.surveys = this.surveys;
    }

    trackByBehaviourKey(_index: number, row: Ed15BehaviourRow): string {
        return row.key;
    }
}
